import type { NodePresence, WorkforceSession } from './data';
import { connectionState, type OperationalStatus } from './presence';

/**
 * How a Worker is named in Settings: "Worker N · <computer>", once.
 *
 * A Worker's stored name is often its own number ("Worker 5") or the computer it
 * was created on ("Windows PC"); printing it beside the number and the computer
 * repeated one or the other. The name is shown only when it adds something.
 */
export function workerComputerLabel(number: number, name: string | null, computer: string | null): string {
  const base = `Worker ${number}`;
  const machine = computer?.trim() || null;
  const own = name?.trim() || null;
  const extra = own === null || own === base || own === machine ? null : own;
  return [base, machine ?? extra].filter((part): part is string => part !== null).join(' · ');
}

export interface WorkforceSessionView {
  readonly session: WorkforceSession;
  readonly node: NodePresence | null;
  /** The session reports offline, or the computer it runs on is not connected. */
  readonly offline: boolean;
}

/**
 * The Worker's current session for display. An offline session is still the
 * Worker's session and is shown as offline - never hidden as "no active session",
 * and never shown as live because its own row has not caught up with the computer.
 */
export function workforceSessionView(
  workerId: string,
  sessions: readonly WorkforceSession[],
  nodes: readonly NodePresence[],
  nowMs: number,
): WorkforceSessionView | null {
  const views = sessions
    .filter((session) => session.worker_id === workerId
      && (session.lifecycle === 'active' || session.lifecycle === 'restored'))
    .map((session) => {
      const node = nodes.find((item) => item.node_id === session.node_id) ?? null;
      const nodeOnline = node !== null
        && connectionState({ lastSeenAt: node.last_seen_at, status: node.status as OperationalStatus, nowMs }) !== 'disconnected';
      return { session, node, offline: session.status === 'offline' || !nodeOnline };
    });
  return views.find((view) => !view.offline) ?? views[0] ?? null;
}

/** The computer a Worker's current session is on, when known. */
export function workerComputer(
  workerId: string,
  sessions: readonly WorkforceSession[],
  nodes: readonly NodePresence[],
  nowMs: number,
): string | null {
  return workforceSessionView(workerId, sessions, nodes, nowMs)?.node?.name ?? null;
}
